import React, { type PropsWithChildren } from 'react';

import * as Modal from '@periodica/ui-kit/modal';
import { useLocation } from '@gatsbyjs/reach-router';

import { Chain, ShareFat as Share } from '@periodica/ui-kit/icons';

import gtm from '@periodica/analytics/gtm';

import { IconButton, Paragraph, Slot, notify } from '@periodica/ui-kit';
import type { DialogStore } from '@ariakit/react';
import { useDialogStore } from '@ariakit/react';

import okIcon from '@images/socialIcons/OK.svg';
import tgIcon from '@images/socialIcons/Telegram.svg';
import vkIcon from '@images/socialIcons/VK.svg';
import waIcon from '@images/socialIcons/WhatsApp.svg';
import shareLinkImage from '@images/ShareLinkImage.svg';

import { useMediaQuery } from '@periodica/hooks';
import * as styles from './styles.module.css';

const baseUrl = process.env.URL || 'https://periodica.press';

const getCategoryName = (caterory: string) => {
  switch (caterory) {
    case 'book':
      return 'фотокнигу';
    case 'prints':
    case 'stickers':
      return 'фотокарточки';
    case 'calendar':
      return 'календарь';
    case 'notebook':
      return 'блокнот';
    case 'gift':
      return 'сертификат';
    default:
      return 'подарок из';
  }
};

const getTextByCategory = (caterory: string) => {
  switch (caterory) {
    case 'main':
      return 'Мне будет приятно получить подарок из Периодики';
    case 'gift':
      return 'Мне будет приятно получить сертификат Периодики в подарок — давно хочу собрать себе фотокнигу';
    default:
      return 'Мне будет приятно получить такой подарок из Периодики';
  }
};

// social = tg | ok | vk | wtap
const getUtmQuery = (social?: string) =>
  `?utm_source=socialsharing${social ? `&utm_medium=${social}` : ''}&utm_campaign=hintgift`;

type ShareLinkModalProps = {
  store: DialogStore;
  onLinkClick: (id: string) => void;
  category?: string;
};

export function ShareLinkButton({
  children,
  category = 'main',
}: PropsWithChildren<{ category?: string }>) {
  const dialogStore = useDialogStore();

  const handleClick = () => {
    dialogStore.show();
    gtm.logEvent({ name: `e_site_share_modal_open` });
  };

  const handleLinkClick = (id: string) => {
    gtm.logEvent({ name: `e_site_share_${id}_click` });
    dialogStore.hide();
  };

  return (
    <>
      <Slot onClick={handleClick}>{children}</Slot>
      <ShareLinkModal store={dialogStore} onLinkClick={handleLinkClick} category={category} />
    </>
  );
}

const getLinksData = (link: string, category: string) => {
  const text = getTextByCategory(category);
  return [
    {
      id: 'vk',
      icon: vkIcon,
      url: `https://vk.com/share.php?url=${encodeURIComponent(`${baseUrl}${link}${getUtmQuery('vk')}`)}&comment=${encodeURIComponent(text)}`,
    },
    {
      id: 'tg',
      icon: tgIcon,
      url: `https://t.me/share/url?url=${encodeURIComponent(`${baseUrl}${link}${getUtmQuery('tg')}`)}&text=${encodeURIComponent(text)}`,
    },
    {
      id: 'wa',
      icon: waIcon,
      url: `https://wa.me/?text=${encodeURIComponent(`${text} ${baseUrl}${link}${getUtmQuery('wtap')}`)}`,
    },
    {
      id: 'ok',
      icon: okIcon,
      url: `https://connect.ok.ru/offer?url=${encodeURIComponent(`${baseUrl}${link}${getUtmQuery('ok')}`)}&title=${encodeURIComponent(text)}`,
    },
  ];
};

export function ShareLinkModal({ store, onLinkClick, category = 'main' }: ShareLinkModalProps) {
  const location = useLocation();
  const path = category === 'main' ? '/products/' : location.pathname;
  const linksData = getLinksData(path, category);

  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(`${getTextByCategory(category)} ${baseUrl}${path}${getUtmQuery()}`)
      .then(() => {
        notify({ text: 'Ссылка на намек скопирована' });
        onLinkClick('copy');
      });
  };

  const handleShareClick = (url: string) => {
    const width = 600;
    const height = 500;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    window.open(
      url,
      'popup',
      `width=${width},height=${height},left=${left},top=${top}, noreferrer=true`
    );
  };

  const handleSystemShareClick = () => {
    if (navigator.share) {
      navigator
        .share({
          title: getTextByCategory(category),
          text: getTextByCategory(category),
          url: `${baseUrl}${path}${getUtmQuery()}`,
          // если пользователь закрыл окно шеринга, падает ошибка
          // мы ее игнорируем, так как этот кейс не нужно обрабатывать
        })
        .catch(() => {});
    }
    onLinkClick('system');
  };

  const isMobile = useMediaQuery('(max-width: 767px)', true);
  const hasShare = typeof window !== 'undefined' && !!navigator?.share;

  return (
    <Modal.Wrapper
      store={store}
      autoFocusOnShow={false}
      size="small"
      type="drawer"
      unmountOnHide
      portal
    >
      <Modal.Header title="Намекните близким о лучшем подарке для вас!" />
      <Modal.Content>
        <div className={styles.shareLinkeModalContent}>
          <div className={styles.imgBlock}>
            <img src={shareLinkImage} alt="Icon" />
          </div>
          <Paragraph size="large" color="primary" className={styles.text}>
            Подскажите им, что будете рады получить {getCategoryName(category)}
            &nbsp;Периодики.
          </Paragraph>
          <Paragraph color="tertiary" weight="emphasized" className={styles.dividerText}>
            <span>Поделиться в мессенджере</span>
          </Paragraph>
          <div className={styles.linkBlock}>
            {isMobile && hasShare && (
              <IconButton
                size="medium"
                type="button"
                variant="brandSecondary"
                className={styles.rounded}
                onClick={handleSystemShareClick}
              >
                <Share />
              </IconButton>
            )}
            {linksData.map((linkItem) => (
              <button
                type="button"
                key={linkItem.id}
                onClick={() => {
                  handleShareClick(linkItem.url);
                  onLinkClick(linkItem.id);
                }}
                className={styles.linkIcon}
              >
                <img src={linkItem.icon} alt="Icon" />
              </button>
            ))}
            <IconButton
              size="medium"
              type="button"
              variant="brandSecondary"
              className={styles.rounded}
              onClick={handleCopyClick}
            >
              <Chain />
            </IconButton>
          </div>
        </div>
      </Modal.Content>
    </Modal.Wrapper>
  );
}
